import { createContext, useState, useMemo, useContext, useEffect } from 'react'
import { useApp } from '@src/AppContext'
import { CREDIT_REPORT_PERMISSION } from '@src/services/Helper'

const EmployeeDetailsSheetContext = createContext()

function EmployeeDetailsSheetProvider({ children }) {
  const { hasPermission, permissionsLoaded } = useApp()
  // Without view_credit_report the BE refuses the Report tab (403), so the
  // sheet runs on Personal Information only. False until /auth/session lands —
  // nothing report-side is fetched before then.
  const canViewReport = hasPermission(...CREDIT_REPORT_PERMISSION)
  const [currentTabs, setCurrentTabs] = useState({
    type: 'report', // 'report' or 'personal_information'
  })

  // Decide only once permissions are known: on a direct load the sheet mounts
  // before the session, and switching early would strand permitted users on
  // Personal Information.
  useEffect(() => {
    if (permissionsLoaded && !canViewReport && currentTabs.type !== 'personal_information') {
      setCurrentTabs({ type: 'personal_information' })
    }
  }, [permissionsLoaded, canViewReport, currentTabs.type])

  const indicators = useMemo(
    () => [
      {
        label: 'Financial Responsibility',
        value: '3 / 10',
        tooltip:
          'Indicates how responsibly the individual manages financial obligations based on credit history, repayment behavior, and overall debt management.',
      },
      {
        label: 'Debt Burden',
        badge: 'High',
        badgeColor: 'error',
        tooltip:
          'Evaluates the level of debt relative to the individual’s financial capacity. Lower debt burden suggests a healthier financial position.',
      },
      {
        label: 'Payment Discipline',
        value: '2 / 10',
        tooltip:
          'Measures consistency in making loan or credit payments on time. Frequent delays may indicate higher financial risk.',
      },
      {
        label: 'Contact Reputation',
        value: '5 / 10',
        tooltip:
          'Assesses the reliability and stability of contact information associated with the individual, including phone number patterns and usage history.',
      },
      {
        label: 'Employment Stability',
        value: '4 / 10',
        tooltip:
          'Reflects the consistency of employment history and job tenure, which may indicate income stability and financial reliability.',
      },
      {
        label: 'Overall Risk',
        badge: 'High',
        badgeColor: 'error',
        tooltip:
          'An aggregated risk evaluation derived from financial behavior, credit history, employment stability, and legal indicators.',
      },
    ],
    []
  )

  // Add more state/logic as needed, following EnquiryContext pattern
  const contextValue = useMemo(
    () => ({
      currentTabs,
      setCurrentTabs,
      canViewReport,
      indicators,
    }),
    [currentTabs, canViewReport, indicators]
  )

  return (
    <EmployeeDetailsSheetContext.Provider value={contextValue}>
      {children}
    </EmployeeDetailsSheetContext.Provider>
  )
}

const useEmployeeDetailsSheet = () => {
  const context = useContext(EmployeeDetailsSheetContext)
  if (!context) {
    throw new Error(
      'useEmployeeDetailsSheet must be used within EmployeeDetailsSheetProvider'
    )
  }
  return context
}

export { EmployeeDetailsSheetProvider, useEmployeeDetailsSheet }
